'use client';

import { Box } from '@ironhack/design-system2/components/box';
import { Flex } from '@ironhack/design-system2/components/flex';
import { type ReactElement, useContext } from 'react';

import { Link } from '@/app/components/client/link';
import { CmsImage } from '@/app/components/server/cms-image';
import { NavColumnHeading } from '../nav-column-heading';
import { NavItemBadge } from '../nav-item-badge';
import { NavItemIcon } from '../nav-item-icon';
import { NavbarContext } from '../navbar-context';
import {
  type ThirdLevelContent,
  type ThirdLevelItem,
  type ThirdLevelSection,
} from '../types';

type Props = {
  isSelected: boolean;
  menu: ThirdLevelContent;
  title: string;
};

export const ThirdLevelMenu = (props: Props): ReactElement | null => {
  const { menu, title, isSelected } = props;

  const { setModalLevelTitle } = useContext(NavbarContext);

  if (isSelected) setModalLevelTitle(title);

  if (!menu?.sections) return null;
  return (
    <>
      {menu.sections.map((section: ThirdLevelSection): ReactElement => {
        const { id, image, menuItems, ...headingContent } = section;
        return (
          <Flex
            _notFirst={{
              borderLeft: [
                null,
                null,
                null,
                '1px solid rgb(255 255 255 / 0.5)',
              ],
              borderTop: [
                '1px solid rgb(255 255 255 / 0.6)',
                null,
                null,
                'none',
              ],
              pl: [null, null, null, 3],
              pt: [4, null, null, 0],
            }}
            direction="column"
            display={isSelected ? 'flex' : 'none'}
            gap={[4, null, null, 3]}
            key={`third-level-section-${id}`}
            w="full"
          >
            <NavColumnHeading {...headingContent} />

            <Flex direction="column" gap={[4, null, null, 3]}>
              {image && (
                <Box
                  alignSelf="flex-start"
                  borderRadius={['lg', null, null, 'sm']}
                  mx={[null, null, null, 0.5]}
                  overflow="hidden"
                >
                  <CmsImage data={image.responsiveImage} />
                </Box>
              )}
              <Flex as="ul" direction="column" gap={2} listStyleType="none">
                {menuItems.map(
                  (item: ThirdLevelItem): ReactElement => (
                    <Flex
                      align="center"
                      as="li"
                      data-level="3"
                      gap={1}
                      justify="space-between"
                      key={`third-level-item-${item.id}`}
                    >
                      <Flex align="center" gap={1}>
                        <Link withoutDecoration {...item.content.target}>
                          {item.label}
                        </Link>
                        {item.badge && <NavItemBadge badge={item.badge} />}
                      </Flex>
                      <NavItemIcon itemType="link" />
                    </Flex>
                  )
                )}
              </Flex>
            </Flex>
          </Flex>
        );
      })}
    </>
  );
};
